import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateDataRequirement,
  CreateDataRequirementValue,
  DataRequirement,
  DataRequirementSummary,
  DataRequirementValue,
  UpdateDataRequirement,
  UpdateDataRequirementStatus,
  UpdateDataRequirementValue,
} from '../types/data-requirements-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const dataRequirementsApi = createApi({
  reducerPath: 'dataRequirementsApi',
  tagTypes: [
    'DataRequirements',
    'DataRequirement',
    'DataRequirementValues',
    'DataRequirementValue',
  ],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // ==================== Task Data Requirements - Primary pattern (by task) ====================

    /**
     * Get all data requirements for a specific task.
     */
    getTaskDataRequirements: builder.query<
      DataRequirement[],
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/data-requirements/`,
      providesTags: (result, error, { matterId, taskId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        ...(result || []).map(({ id }) => ({
          type: 'DataRequirement' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new data requirement for a task.
     */
    createTaskDataRequirement: builder.mutation<
      DataRequirement,
      {
        matterId: string;
        taskId: string;
        dataRequirement: Omit<CreateDataRequirement, 'task_id'>;
      }
    >({
      query: ({ matterId, taskId, dataRequirement }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/`,
        method: 'POST',
        body: { ...dataRequirement, task_id: taskId },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
      ],
    }),

    /**
     * Get a specific data requirement by task.
     */
    getTaskDataRequirement: builder.query<
      DataRequirement,
      { matterId: string; taskId: string; requirementId: string }
    >({
      query: ({ matterId, taskId, requirementId }) =>
        `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/`,
      providesTags: (result, error, { requirementId }) => [
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Update a data requirement (full update).
     */
    updateTaskDataRequirement: builder.mutation<
      DataRequirement,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        dataRequirement: UpdateDataRequirement;
      }
    >({
      query: ({ matterId, taskId, requirementId, dataRequirement }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/`,
        method: 'PUT',
        body: dataRequirement,
      }),
      invalidatesTags: (result, error, { taskId, requirementId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Partially update a data requirement.
     */
    partialUpdateTaskDataRequirement: builder.mutation<
      DataRequirement,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        dataRequirement: Partial<UpdateDataRequirement>;
      }
    >({
      query: ({ matterId, taskId, requirementId, dataRequirement }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/`,
        method: 'PATCH',
        body: dataRequirement,
      }),
      invalidatesTags: (result, error, { taskId, requirementId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Delete a data requirement.
     */
    deleteTaskDataRequirement: builder.mutation<
      void,
      { matterId: string; taskId: string; requirementId: string }
    >({
      query: ({ matterId, taskId, requirementId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId, requirementId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Bypass a data requirement (mark as bypassed by user).
     */
    bypassDataRequirement: builder.mutation<
      DataRequirement,
      { matterId: string; taskId: string; requirementId: string }
    >({
      query: ({ matterId, taskId, requirementId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/bypass/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { taskId, requirementId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Get data requirement summary for a task.
     */
    getTaskDataRequirementSummary: builder.query<
      DataRequirementSummary,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/data-requirements/summary/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'DataRequirements', id: `task-${taskId}-summary` },
      ],
    }),

    // ==================== Matter Data Requirements - Convenience pattern (all requirements across tasks) ====================

    /**
     * Get all data requirements for a matter (across all tasks).
     */
    getMatterDataRequirements: builder.query<
      DataRequirement[],
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/data-requirements/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'DataRequirements', id: `matter-${matterId}` },
        ...(result || []).map(({ id }) => ({
          type: 'DataRequirement' as const,
          id,
        })),
      ],
    }),

    /**
     * Get a specific data requirement by matter.
     */
    getMatterDataRequirement: builder.query<
      DataRequirement,
      { matterId: string; requirementId: string }
    >({
      query: ({ matterId, requirementId }) =>
        `api/matters/${matterId}/data-requirements/${requirementId}/`,
      providesTags: (result, error, { requirementId }) => [
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Batch create multiple data requirements for a matter.
     */
    batchCreateDataRequirements: builder.mutation<
      DataRequirement[],
      { matterId: string; dataRequirements: CreateDataRequirement[] }
    >({
      query: ({ matterId, dataRequirements }) => ({
        url: `api/matters/${matterId}/data-requirements/batch_create/`,
        method: 'POST',
        body: dataRequirements,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'DataRequirements', id: `matter-${matterId}` },
      ],
    }),

    /**
     * Get pending data requirements for a matter.
     */
    getPendingMatterDataRequirements: builder.query<
      DataRequirement[],
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/data-requirements/pending/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'DataRequirements', id: `matter-${matterId}-pending` },
      ],
    }),

    /**
     * Get data requirement summary for a matter.
     */
    getMatterDataRequirementSummary: builder.query<
      DataRequirementSummary,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/data-requirements/summary/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'DataRequirements', id: `matter-${matterId}-summary` },
      ],
    }),

    // ==================== Data Requirement Values ====================

    /**
     * Get all values for a specific data requirement.
     */
    getDataRequirementValues: builder.query<
      DataRequirementValue[],
      {
        matterId: string;
        taskId: string;
        requirementId: string;
      }
    >({
      query: ({ matterId, taskId, requirementId }) =>
        `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/`,
      providesTags: (result, error, { requirementId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        ...(result || []).map(({ id }) => ({
          type: 'DataRequirementValue' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new data requirement value.
     */
    createDataRequirementValue: builder.mutation<
      DataRequirementValue,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        value: CreateDataRequirementValue;
        agent?: string;
      }
    >({
      query: ({ matterId, taskId, requirementId, value, agent }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/`,
        method: 'POST',
        body: agent ? { ...value, agent } : value,
      }),
      invalidatesTags: (result, error, { requirementId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Get a specific data requirement value.
     */
    getDataRequirementValue: builder.query<
      DataRequirementValue,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        valueId: string;
      }
    >({
      query: ({ matterId, taskId, requirementId, valueId }) =>
        `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/${valueId}/`,
      providesTags: (result, error, { valueId }) => [
        { type: 'DataRequirementValue', id: valueId },
      ],
    }),

    /**
     * Update a data requirement value (full update).
     */
    updateDataRequirementValue: builder.mutation<
      DataRequirementValue,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        valueId: string;
        value: UpdateDataRequirementValue;
      }
    >({
      query: ({ matterId, taskId, requirementId, valueId, value }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/${valueId}/`,
        method: 'PUT',
        body: value,
      }),
      invalidatesTags: (result, error, { requirementId, valueId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        { type: 'DataRequirementValue', id: valueId },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Partially update a data requirement value.
     */
    partialUpdateDataRequirementValue: builder.mutation<
      DataRequirementValue,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        valueId: string;
        value: Partial<UpdateDataRequirementValue>;
      }
    >({
      query: ({ matterId, taskId, requirementId, valueId, value }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/${valueId}/`,
        method: 'PATCH',
        body: value,
      }),
      invalidatesTags: (result, error, { requirementId, valueId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        { type: 'DataRequirementValue', id: valueId },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Delete a data requirement value.
     */
    deleteDataRequirementValue: builder.mutation<
      void,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        valueId: string;
      }
    >({
      query: ({ matterId, taskId, requirementId, valueId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/${valueId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { requirementId, valueId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        { type: 'DataRequirementValue', id: valueId },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Approve a data requirement value.
     */
    approveDataRequirementValue: builder.mutation<
      DataRequirementValue,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        valueId: string;
      }
    >({
      query: ({ matterId, taskId, requirementId, valueId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/${valueId}/approve/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { requirementId, valueId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        { type: 'DataRequirementValue', id: valueId },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    /**
     * Reject a data requirement value.
     */
    rejectDataRequirementValue: builder.mutation<
      DataRequirementValue,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        valueId: string;
      }
    >({
      query: ({ matterId, taskId, requirementId, valueId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/values/${valueId}/reject/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { requirementId, valueId }) => [
        { type: 'DataRequirementValues', id: requirementId },
        { type: 'DataRequirementValue', id: valueId },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),

    // ==================== Collection Operations ====================

    /**
     * Batch extract values for multiple data requirements.
     */
    batchExtractDataRequirementValues: builder.mutation<
      DataRequirementValue[],
      {
        matterId: string;
        taskId: string;
        values: CreateDataRequirementValue[];
        agent?: string;
      }
    >({
      query: ({ matterId, taskId, values, agent }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/values/batch_extract/`,
        method: 'POST',
        body: agent ? values.map((v) => ({ ...v, agent })) : values,
      }),
      invalidatesTags: (result, error, { matterId, taskId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        { type: 'DataRequirements', id: `matter-${matterId}` },
      ],
    }),

    /**
     * Get values pending review for a task.
     */
    getPendingReviewValues: builder.query<
      DataRequirementValue[],
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/data-requirements/values/pending_review/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'DataRequirementValues', id: `task-${taskId}-pending` },
      ],
    }),

    // ==================== Status Update Operations ====================

    /**
     * Update only the status of a data requirement.
     */
    updateDataRequirementStatus: builder.mutation<
      DataRequirement,
      {
        matterId: string;
        taskId: string;
        requirementId: string;
        status: UpdateDataRequirementStatus;
      }
    >({
      query: ({ matterId, taskId, requirementId, status }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/data-requirements/${requirementId}/`,
        method: 'PATCH',
        body: status,
      }),
      invalidatesTags: (result, error, { taskId, requirementId }) => [
        { type: 'DataRequirements', id: `task-${taskId}` },
        { type: 'DataRequirement', id: requirementId },
      ],
    }),
  }),
});

// Export all the auto-generated hooks
export const {
  // Task data requirements
  useGetTaskDataRequirementsQuery,
  useCreateTaskDataRequirementMutation,
  useGetTaskDataRequirementQuery,
  useUpdateTaskDataRequirementMutation,
  usePartialUpdateTaskDataRequirementMutation,
  useDeleteTaskDataRequirementMutation,
  useBypassDataRequirementMutation,
  useGetTaskDataRequirementSummaryQuery,

  // Matter data requirements
  useGetMatterDataRequirementsQuery,
  useGetMatterDataRequirementQuery,
  useBatchCreateDataRequirementsMutation,
  useGetPendingMatterDataRequirementsQuery,
  useGetMatterDataRequirementSummaryQuery,

  // Data requirement values
  useGetDataRequirementValuesQuery,
  useCreateDataRequirementValueMutation,
  useGetDataRequirementValueQuery,
  useUpdateDataRequirementValueMutation,
  usePartialUpdateDataRequirementValueMutation,
  useDeleteDataRequirementValueMutation,
  useApproveDataRequirementValueMutation,
  useRejectDataRequirementValueMutation,

  // Collection operations
  useBatchExtractDataRequirementValuesMutation,
  useGetPendingReviewValuesQuery,

  // Status operations
  useUpdateDataRequirementStatusMutation,
} = dataRequirementsApi;
